import jsPDF from 'jspdf';
import { PortvilleBill } from '../types/portvilleBilling';

// Public asset path resolver (works with Vite BASE_URL, CRA PUBLIC_URL, or root)
function resolvePublicAssetPath(relPath: string): string {
  const cleanedRel = relPath.replace(/^\//, '');
  // 1) CRA: PUBLIC_URL 우선
  try {
    const pub: string | undefined = (typeof process !== 'undefined' && (process as any).env && (process as any).env.PUBLIC_URL)
      ? (process as any).env.PUBLIC_URL
      : undefined;
    if (pub) {
      const p = pub.endsWith('/') ? pub.slice(0, -1) : pub;
      return `${p}/${cleanedRel}`;
    }
  } catch (_) {}
  // 2) Vite: BASE_URL
  try {
    // @ts-ignore
    const base: string | undefined = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.BASE_URL)
      ? (import.meta as any).env.BASE_URL
      : undefined;
    if (base) {
      const b = base.endsWith('/') ? base.slice(0, -1) : base;
      return `${b}/${cleanedRel}`;
    }
  } catch (_) {}
  // 3) 현재 오리진 기준으로 절대 경로 생성
  try {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      const origin = window.location.origin.replace(/\/$/, '');
      return `${origin}/${cleanedRel}`;
    }
  } catch (_) {}
  // 4) 최종 폴백: 루트 기준 절대 경로
  return `/${cleanedRel}`;
}

/**
 * 청구서 PDF 생성 유틸리티
 *
 * 참고: 한글 폰트는 public/fonts/NotoSansKR-Regular.ttf 및 NotoSansKR-Bold.ttf 를 사용합니다.
 *       배포 환경(서버/도커)에도 해당 파일이 제공되어야 합니다.
 */
export class BillPDFGenerator {
  private doc: jsPDF;
  private encoding: 'EUC-KR' | 'UTF-8';
  private hasBoldFont: boolean = false;
  private isKoreanFontReady: boolean = false;

  constructor(encoding: 'EUC-KR' | 'UTF-8' = 'UTF-8') {
    this.doc = new jsPDF();
    this.encoding = encoding;
  }

  /**
   * 한글 폰트 설정 - Noto Sans KR 안정적 로딩 (개선된 방법)
   */
  private async setupKoreanFont(): Promise<boolean> {
    console.log('[PDF] 한글 폰트 설정 시작...');
    
    const blobToBase64 = (blob: Blob): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

    try {
      // 1. Noto Sans KR Regular 폰트 로딩
      const regularUrl = resolvePublicAssetPath('fonts/NotoSansKR-Regular.ttf');
      console.log('[PDF] 폰트 URL:', regularUrl);
      
      const response = await fetch(regularUrl, { cache: 'no-store' as RequestCache });
      if (!response.ok) {
        throw new Error(`폰트 로딩 실패: HTTP ${response.status}`);
      }
      
      const fontBlob = await response.blob();
      const fontBase64 = await blobToBase64(fontBlob);
      
      console.log('[PDF] 폰트 크기:', fontBlob.size, 'bytes');
      console.log('[PDF] Base64 길이:', fontBase64.length);
      
      // 파일 크기 기초 검증 (너무 작으면 잘못된 파일로 판단)
      const MIN_VALID_TTF_BYTES = 100000; // 100KB 미만이면 비정상으로 간주
      if (fontBlob.size < MIN_VALID_TTF_BYTES) {
        console.warn('[PDF] Regular 폰트 크기 비정상, 한글 폰트 비활성화:', fontBlob.size, 'bytes');
        this.isKoreanFontReady = false;
        return false;
      }

      // 2. Bold 폰트도 로딩 시도
      let boldFontBase64: string | null = null;
      try {
        const boldUrl = resolvePublicAssetPath('fonts/NotoSansKR-Bold.ttf');
        console.log('[PDF] Bold 폰트 URL:', boldUrl);
        
        const boldResponse = await fetch(boldUrl, { cache: 'no-store' as RequestCache });
        if (boldResponse.ok) {
          const boldBlob = await boldResponse.blob();
          if (boldBlob.size >= MIN_VALID_TTF_BYTES) {
            boldFontBase64 = await blobToBase64(boldBlob);
            console.log('[PDF] Bold 폰트 로딩 성공, 크기:', boldBlob.size, 'bytes');
          } else {
            console.warn('[PDF] Bold 폰트 크기 비정상, Bold 비활성화:', boldBlob.size, 'bytes');
          }
        }
      } catch (boldError) {
        console.warn('[PDF] Bold 폰트 로딩 실패 (계속 진행):', boldError);
      }

      // 3. jsPDF에 폰트 등록
      try {
        // Regular 폰트 등록 (Identity-H 인코딩 지정)
        this.doc.addFileToVFS('NotoSansKR-Regular.ttf', fontBase64);
        (this.doc as any).addFont('NotoSansKR-Regular.ttf', 'NotoSansKR', 'normal', 'Identity-H');
        console.log('[PDF] Regular 폰트 등록 성공');
        
        // Bold 폰트 등록 (있을 경우)
        if (boldFontBase64) {
          this.doc.addFileToVFS('NotoSansKR-Bold.ttf', boldFontBase64);
          (this.doc as any).addFont('NotoSansKR-Bold.ttf', 'NotoSansKR', 'bold', 'Identity-H');
          this.hasBoldFont = true;
          console.log('[PDF] Bold 폰트 등록 성공');
        } else {
          this.hasBoldFont = false;
          console.warn('[PDF] Bold 폰트 없음 - Regular 폰트로 bold 스타일 처리');
        }
        
        // 4. 폰트 설정
        if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'normal');
        this.doc.setFontSize(10);
        
        // PDF 메타데이터 설정
        this.doc.setProperties({
          title: '포트빌 관리비 청구서',
          subject: '관리비 청구서',
          author: '포트빌 관리사무소',
          creator: '포트빌 관리 시스템',
          keywords: '포트빌, 관리비, 청구서'
        });
        
        console.log('[PDF] Noto Sans KR 설정 완료');
        this.isKoreanFontReady = true;
        return true;
        
      } catch (fontError) {
        console.error('[PDF] 폰트 등록 실패:', fontError);
        throw fontError;
      }
      
    } catch (error) {
      console.error('[PDF] Noto Sans KR 설정 실패:', error);
      
      // 백업: 시스템 기본 폰트 시도
      try {
        console.log('[PDF] 시스템 기본 폰트로 백업 시도...');
        
        // 더 나은 한글 지원을 위해 times 또는 courier 시도
        const fallbackFonts = ['times', 'courier', 'helvetica'];
        
        for (const font of fallbackFonts) {
          try {
            this.doc.setFont(font);
            console.log(`[PDF] ${font} 폰트로 설정됨`);
            break;
          } catch (fontError) {
            console.warn(`[PDF] ${font} 폰트 실패:`, fontError);
          }
        }
        
        return false;
      } catch (fallbackError) {
        console.error('[PDF] 모든 폰트 설정 실패:', fallbackError);
        return false;
      }
    }
  }

  /**
   * 한글 텍스트를 PDF에 안전하게 추가하는 메서드
   */
  private addKoreanText(
    text: string,
    x: number,
    y: number,
    options?: any,
    style: 'normal' | 'bold' = 'normal'
  ): void {
    if (!text) {
      return;
    }

    try {
      // 한글 텍스트를 정규화
      const normalizedText = this.normalizeText(text);
      
      // 준비된 경우에만 NotoSansKR 사용
      if (this.isKoreanFontReady) {
        if (style === 'bold' && this.hasBoldFont) {
          if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');
        } else {
          if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'normal');
        }
      }
      
      this.doc.text(normalizedText, x, y, options);
      
    } catch (notoError) {
      // NotoSansKR 실패 시 백업 처리
      console.warn('[PDF] NotoSansKR 렌더링 실패, 백업 시도:', text, notoError);
      
      try {
        // 백업 1: times 폰트로 시도
        this.doc.setFont('times', style === 'bold' ? 'bold' : 'normal');
        this.doc.text(text, x, y, options);
        
      } catch (timesError) {
        console.warn('[PDF] times 폰트 실패, helvetica로 시도:', timesError);
        
        try {
          // 백업 2: helvetica로 시도
          this.doc.setFont('helvetica', style === 'bold' ? 'bold' : 'normal');
          this.doc.text(text, x, y, options);
          
        } catch (helveticaError) {
          console.error('[PDF] 모든 폰트 실패, 기본 렌더링:', helveticaError);
          
          try {
            // 최종 백업: 기본 폰트
            this.doc.setFont('helvetica');
            this.doc.text(text, x, y, options);
          } catch (finalError) {
            console.error('[PDF] 최종 텍스트 렌더링 실패:', finalError);
          }
        }
      }
    }
  }

  /**
   * 텍스트 정규화 (Unicode NFC 형식으로 정규화)
   */
  private normalizeText(text: string): string {
    if (!text) return '';
    
    try {
      // Unicode NFC (Normalization Form Canonical Composition) 정규화
      // 한글의 경우 자음+모음을 하나의 문자로 결합
      return text.normalize('NFC');
    } catch (error) {
      console.warn('[PDF] 텍스트 정규화 실패:', error);
      return text;
    }
  }



  /**
   * 청구서 PDF 생성
   */
  generateBillPDF(bill: PortvilleBill): void {
    // Render a single bill **onto the current document/page**
    // Caller is responsible for pagination and final save

    try {
      // 기본 폰트 사이즈만 설정 (폰트 선택은 addKoreanText가 수행)
      this.doc.setFontSize(10);
      console.log('[PDF] 청구서 생성 시작 - 폰트: NotoSansKR');
      
      // 헤더
      this.generateHeader(bill);

      // 안내문
      this.generateNotice(bill);

      // 청구 항목
      this.generateBillItems(bill);

      // 합계
      this.generateTotal(bill);

      // 푸터
      this.generateFooter(bill);
      
      console.log('[PDF] 청구서 생성 완료');
      
    } catch (error) {
      console.error('[PDF] 청구서 생성 중 오류 발생:', error);
      
      // 오류 발생 시 최소한의 정보라도 출력
      try {
        this.doc.setFont('helvetica');
        this.doc.setFontSize(12);
        
        // 간단한 청구서 정보 표시
        this.addKoreanText('포트빌 관리비 청구서', 20, 20, undefined, 'bold');
        this.addKoreanText(`호실: ${bill.roomNumber}`, 20, 40);
        this.addKoreanText(`청구기간: ${bill.billingPeriod.year}년 ${bill.billingPeriod.month}월`, 20, 60);
        this.addKoreanText(`총 금액: ${this.formatCurrency(Number(bill.totalAmount))}`, 20, 80);
        this.addKoreanText(`납부기한: ${new Date(bill.dueDate).toLocaleDateString('ko-KR')}`, 20, 100);
        
      } catch (fallbackError) {
        console.error('[PDF] 최소 정보 출력도 실패:', fallbackError);
        throw fallbackError;
      }
    }
  }


  /**
   * 헤더 생성
   */
  private generateHeader(bill: PortvilleBill): void {
    try {
      // 제목
      this.doc.setFontSize(20);
      if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');
      this.addKoreanText(`복지플러스 ${bill.billingPeriod.month}월분 관리비 고지서 및 내역서`, 105, 20, { align: 'center' }, 'bold');

      // 호실정보 (제목과 같은 폰트 크기)
      this.addKoreanText(`${bill.roomNumber}호`, 105, 30, { align: 'center' }, 'bold');

      // 구분선 (호실정보와 10pt 간격)
      this.doc.setLineWidth(0.5);
      this.doc.line(20, 35, 190, 35);

      
    } catch (error) {
      console.error('[PDF] 헤더 생성 실패:', error);
    }
  }

  /**
   * 안내문 생성
   */
  private generateNotice(bill: PortvilleBill): void {
    try {
      const startY = 45; // 10pt 위로 이동

        // 섹션 제목 (박스 너비에 맞춰 중앙 정렬 조정)
        this.doc.setFontSize(14);
        if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');
        this.addKoreanText('안내문', 15 + 175/2, startY, { align: 'center' }, 'bold'); // 100에서 15 + 175/2 = 102.5로 변경

      // 안내문 내용
      this.doc.setFontSize(10);
      if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'normal');
      
      const noticeItems = [
        '1. 청구 금액을 납기 기한 안에 납부하여 주시기 바랍니다',
        '2. 청구 금액과 실 입금액이 상이할 경우 납부가 안 되오니, 같은 금액으로 납부하여 주시기 바랍니다',
        '3. 납부 기한이 경과하면 연체료를 가산 징수하오니 납부기한 내에 납부하여 주시기 바랍니다',
        '4. 월 임대료 현금영수증 신청 세대는 매월 납부기한 내에 납부 후 관리실 (051-890-7455) 로 연락 바랍니다'
      ];

      noticeItems.forEach((item, index) => {
        const y = startY + 10 + (index * 6);
        this.addKoreanText(`• ${item}`, 20, y);
      });

      // 납부 기한일과 부과 기간 정보
      const infoStartY = startY + 10 + (noticeItems.length * 6) + 10;
      
      // 납부 기한일과 부과 기간을 2줄로 나누어 좌우 배치
      const dueDate = new Date(bill.dueDate);
      const dueDateStr = `${dueDate.getFullYear()}년 ${dueDate.getMonth() + 1}월 ${dueDate.getDate()}일`;
      
      // 부과 기간 (청구 기간의 첫날과 마지막날)
      const periodStart = new Date(bill.billingPeriod.year, bill.billingPeriod.month - 1, 1);
      const periodEnd = new Date(bill.billingPeriod.year, bill.billingPeriod.month, 0);
      const periodStartStr = `${periodStart.getFullYear()}년 ${periodStart.getMonth() + 1}월 ${periodStart.getDate()}일`;
      const periodEndStr = `${periodEnd.getFullYear()}년 ${periodEnd.getMonth() + 1}월 ${periodEnd.getDate()}일`;
      
      // 첫 번째 줄: 납부 기한일 (좌측) + 부과기간 (우측)
      this.addKoreanText(`■ 납부 기한일 : ${dueDateStr}`, 20, infoStartY);
      this.addKoreanText(`■ 부과기간`, 120, infoStartY);
      
      // 두 번째 줄: 문의전화 (좌측) + 부과 기간 날짜 (우측)
      this.addKoreanText(`☎ 문의전화 : 051-890-7455`, 20, infoStartY + 8);
      this.addKoreanText(`    ${periodStartStr} ~ ${periodEndStr}`, 120, infoStartY + 8);
      
        // 안내문 섹션 전체를 박스로 감싸기 (청구 내역 박스와 동일한 너비)
        const boxStartY = startY - 5; // 제목 위쪽 여백
        const boxEndY = infoStartY + 8 + 5; // 마지막 내용 아래쪽 여백
        const boxHeight = boxEndY - boxStartY;
        const boxWidth = 175; // 청구 내역 박스와 동일한 너비 (170에서 175로 변경)

        this.doc.setLineWidth(0.3);
        this.doc.rect(15, boxStartY, boxWidth, boxHeight);
      
    } catch (error) {
      console.error('[PDF] 안내문 생성 실패:', error);
    }
  }

  /**
   * 사용량 정보 생성 (표 형태)
   */
  private generateUsageInfo(bill: PortvilleBill): void {
    if (!bill.usageInfo) return;

    try {
      // 청구 항목의 오른쪽, 납부처 안내 아래쪽에 배치 (10pt 위로 이동, 우측으로 5pt 이동)
      const startX = 105; // 100에서 105로 변경
      const startY = 190; // 납부처 안내 아래쪽 (200에서 190으로 변경)

      // 사용량 정보를 개별 박스로 구성 (여백 없이 붙여서 표처럼, 너비 10pt 줄임)
      const boxWidth = 80; // 90에서 80으로 변경
      const boxHeight = 8;
      
      this.doc.setLineWidth(0.3);
      this.doc.setFontSize(8);
      if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');

      // 1. 사용량 제목 박스 (전체 너비)
      this.doc.rect(startX, startY - 5, boxWidth, boxHeight);
      this.addKoreanText('사용량', startX + boxWidth / 2, startY, { align: 'center' }, 'bold');

      // 2. 전기 섹션
      const electricityY = startY + boxHeight;
      this.doc.rect(startX, electricityY - 5, boxWidth * 0.4, boxHeight);
      this.addKoreanText('전기', startX + (boxWidth * 0.4) / 2, electricityY, { align: 'center' }, 'bold');

      // 3. 수도 섹션
      const waterX = startX + boxWidth * 0.4;
      this.doc.rect(waterX, electricityY - 5, boxWidth * 0.4, boxHeight);
      this.addKoreanText('수도', waterX + (boxWidth * 0.4) / 2, electricityY, { align: 'center' }, 'bold');

      // 4. 인터넷 섹션
      const internetX = startX + boxWidth * 0.8;
      this.doc.rect(internetX, electricityY - 5, boxWidth * 0.2, boxHeight);
      this.addKoreanText('인터넷', internetX + (boxWidth * 0.2) / 2, electricityY, { align: 'center' }, 'bold');

      // 5. 세 번째 행: 개별, 공용, 개별, 공용, 신청
      const thirdRowY = electricityY + boxHeight;
      
      // 전기 개별 박스
      this.doc.rect(startX, thirdRowY - 5, (boxWidth * 0.4) / 2, boxHeight);
      this.addKoreanText('개별', startX + (boxWidth * 0.4) / 4, thirdRowY, { align: 'center' }, 'bold');

      // 전기 공용 박스
      this.doc.rect(startX + (boxWidth * 0.4) / 2, thirdRowY - 5, (boxWidth * 0.4) / 2, boxHeight);
      this.addKoreanText('공용', startX + (boxWidth * 0.4) * 3 / 4, thirdRowY, { align: 'center' }, 'bold');

      // 수도 개별 박스
      this.doc.rect(waterX, thirdRowY - 5, (boxWidth * 0.4) / 2, boxHeight);
      this.addKoreanText('개별', waterX + (boxWidth * 0.4) / 4, thirdRowY, { align: 'center' }, 'bold');

      // 수도 공용 박스
      this.doc.rect(waterX + (boxWidth * 0.4) / 2, thirdRowY - 5, (boxWidth * 0.4) / 2, boxHeight);
      this.addKoreanText('공용', waterX + (boxWidth * 0.4) * 3 / 4, thirdRowY, { align: 'center' }, 'bold');

      // 인터넷 신청 박스
      this.doc.rect(internetX, thirdRowY - 5, boxWidth * 0.2, boxHeight);
      this.addKoreanText('신청', internetX + (boxWidth * 0.2) / 2, thirdRowY, { align: 'center' }, 'bold');

      // 6. 네 번째 행: 실제 값들 (박스로 구현)
      const valuesY = thirdRowY + boxHeight;
      
      // 전기 개별 값 박스
      const electricityIndividualValue = bill.usageInfo.electricityIndividualUsage !== undefined && bill.usageInfo.electricityIndividualUsage !== null
        ? bill.usageInfo.electricityIndividualUsage.toLocaleString()
        : '-';
      this.doc.rect(startX, valuesY - 5, (boxWidth * 0.4) / 2, boxHeight);
      this.addKoreanText(electricityIndividualValue, startX + (boxWidth * 0.4) / 4, valuesY, { align: 'center' });

      // 전기 공용 값 박스
      const electricityCommonValue = bill.usageInfo.electricityCommonUsage !== undefined && bill.usageInfo.electricityCommonUsage !== null
        ? bill.usageInfo.electricityCommonUsage.toLocaleString()
        : '-';
      this.doc.rect(startX + (boxWidth * 0.4) / 2, valuesY - 5, (boxWidth * 0.4) / 2, boxHeight);
      this.addKoreanText(electricityCommonValue, startX + (boxWidth * 0.4) * 3 / 4, valuesY, { align: 'center' });

      // 수도 개별 값 박스
      const waterIndividualValue = bill.usageInfo.waterIndividualUsage !== undefined && bill.usageInfo.waterIndividualUsage !== null
        ? bill.usageInfo.waterIndividualUsage.toLocaleString()
        : '-';
      this.doc.rect(waterX, valuesY - 5, (boxWidth * 0.4) / 2, boxHeight);
      this.addKoreanText(waterIndividualValue, waterX + (boxWidth * 0.4) / 4, valuesY, { align: 'center' });

      // 수도 공용 값 박스
      const waterCommonValue = bill.usageInfo.waterCommonUsage !== undefined && bill.usageInfo.waterCommonUsage !== null
        ? bill.usageInfo.waterCommonUsage.toLocaleString()
        : '-';
      this.doc.rect(waterX + (boxWidth * 0.4) / 2, valuesY - 5, (boxWidth * 0.4) / 2, boxHeight);
      this.addKoreanText(waterCommonValue, waterX + (boxWidth * 0.4) * 3 / 4, valuesY, { align: 'center' });

      // 인터넷 신청 값 박스
      const internetStatus = Number(bill.billItems.communicationFee) === 0 ? 'X' : 'O';
      this.doc.rect(internetX, valuesY - 5, boxWidth * 0.2, boxHeight);
      this.addKoreanText(internetStatus, internetX + (boxWidth * 0.2) / 2, valuesY, { align: 'center' });

    } catch (error) {
      console.error('[PDF] 사용량 정보 생성 중 오류:', error);
    }
  }

  /**
   * 청구 항목 생성
   */
  private generateBillItems(bill: PortvilleBill): void {
    try {
      // 안내문 마지막에서 10pt 간격으로 시작 (10pt 위로 이동)
      const startY = 55; // 안내문 시작 위치 (60에서 50으로 변경)
      const noticeItemsLength = 4; // 안내문 항목 개수
      const infoSectionHeight = 16; // 납부 기한일과 부과 기간 정보 높이
      const finalStartY = startY + 10 + (noticeItemsLength * 6) + 10 + infoSectionHeight; // 안내문 마지막 + 10pt

      // 섹션 제목 (중앙 정렬)
      this.doc.setFontSize(14);
      if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');
      this.addKoreanText('청구 내역', 105, finalStartY, { align: 'center' }, 'bold');


      // 당월 청구 항목 데이터
      const monthlyItems = [
        ['* 월임대료', bill.billItems.monthlyRent],
        ['* 관리비', bill.billItems.managementFee],
        ['  부가세', bill.billItems.managementFeeVAT],
        ['* 전기요금', bill.billItems.electricityFee],
        ['  부가세', bill.billItems.electricityFeeVAT],
        ['* 전력기금', bill.billItems.electricityFund],
        ['* 수도요금', bill.billItems.waterFee],
        ['* 통신요금', bill.billItems.communicationFee],
        ['  부가세', bill.billItems.communicationFeeVAT],
        ['* 상계처리비', bill.billItems.offsetProcessingFee],
        ['  부가세', bill.billItems.offsetProcessingFeeVAT]
      ];

      // 당월 청구금액 합계 계산
      const monthlyTotal = monthlyItems.reduce((sum, [, amount]) => sum + Number(amount), 0);
      monthlyItems.push(['합계', monthlyTotal]);

      // 미납/연체 관련 항목
      const overdueItems = [
        ['* 미납금', bill.billItems.overdueAmount || 0],
        ['* 연체 가산금', bill.billItems.overduePenalty || 0]
      ];

      // 미납/연체 합계 계산
      const overdueTotal = overdueItems.reduce((sum, [, amount]) => sum + Number(amount), 0);
      overdueItems.push(['합계', overdueTotal]);

      // 폰트 크기 설정
      this.doc.setFontSize(10);

      // 당월 청구 항목 표시 (우측으로 5pt 이동)
      monthlyItems.forEach(([item, amount], index) => {
        const y = finalStartY + 15 + (index * 6);
        
        // 금액이 음수인 경우 적색으로 표시
        const isNegativeAmount = Number(amount) < 0;
        if (isNegativeAmount) {
          this.doc.setTextColor(255, 0, 0); // 적색
        }
        
        // 부가세 항목이 아닌 경우 Bold 처리
        const isVATItem = String(item).includes('부가세');
        const isSumItem = String(item).includes('합계');
        
        this.addKoreanText(String(item), 25, y, undefined, isVATItem ? 'normal' : 'bold'); // 20에서 25로 변경
        this.addKoreanText(this.formatCurrency(Number(amount)), 95, y, { align: 'right' }, isVATItem ? 'normal' : 'bold'); // 90에서 95로 변경
        
        // 색상을 다시 검은색으로 복원
        if (isNegativeAmount) {
          this.doc.setTextColor(0, 0, 0); // 검은색
        }
      });

      // 미납/연체 항목을 별도 박스로 표시 (시작위치 8pt 위로)
      const overdueStartY = finalStartY + 15 + (monthlyItems.length * 6) + 7; // 15에서 7로 변경 (8pt 위로)
      
      // 미납/연체 박스 그리기 (높이 3pt 줄임, 우측으로 5pt 이동)
      const overdueBoxHeight = (overdueItems.length * 6) + 2; // 5에서 2로 변경 (3pt 줄임)
      this.doc.setLineWidth(0.3);
      this.doc.rect(20, overdueStartY - 3, 80, overdueBoxHeight); // 15에서 20으로 변경

      // 미납/연체 항목들 (우측으로 5pt 이동)
      overdueItems.forEach(([item, amount], index) => {
        const y = overdueStartY + 2 + (index * 6);
        
        // 금액이 음수인 경우 적색으로 표시
        const isNegativeAmount = Number(amount) < 0;
        if (isNegativeAmount) {
          this.doc.setTextColor(255, 0, 0); // 적색
        }
        
        const isSumItem = String(item).includes('합계');
        
        this.addKoreanText(String(item), 25, y, undefined, 'bold'); // 20에서 25로 변경
        this.addKoreanText(this.formatCurrency(Number(amount)), 95, y, { align: 'right' }, 'bold'); // 90에서 95로 변경
        
        // 색상을 다시 검은색으로 복원
        if (isNegativeAmount) {
          this.doc.setTextColor(0, 0, 0); // 검은색
        }
      });



      // 당월 청구 항목 영역 사각형 (높이 5pt 줄임, 우측으로 5pt 이동)
      const monthlyRectStartY = finalStartY + 15 - 6; // 첫 번째 항목 위쪽 여백 6pt
      const monthlyRectEndY = finalStartY + 15 + (monthlyItems.length * 6) - 2; // 당월 항목 아래쪽 여백 (3pt에서 -2pt로 변경)
      const monthlyRectHeight = monthlyRectEndY - monthlyRectStartY;
      
      this.doc.setLineWidth(0.3);
      this.doc.rect(20, monthlyRectStartY, 80, monthlyRectHeight); // 당월 청구 항목 영역 (15에서 20으로 변경)
      
      // 사용량 정보 표시 (청구 항목 오른쪽)
      this.generateUsageInfo(bill);
      
      // 우측 영역에 납부처 안내 추가 (우측으로 5pt 이동)
      const rightAreaX = 105; // 100에서 105로 변경
      const rightAreaStartY = monthlyRectStartY + 5;
      
      // 납부처 안내 제목 (중앙 정렬, 박스 너비에 맞춰 조정)
      this.doc.setFontSize(12);
      if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');
      this.addKoreanText('납부처 안내', rightAreaX + 40, rightAreaStartY, { align: 'center' }, 'bold'); // 45에서 40으로 변경

      // 안내 내용 (중앙 정렬, 박스 너비에 맞춰 조정)
      this.doc.setFontSize(10);
      if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');
        this.addKoreanText('오픈지로(카카오톡) 을 확인 후', rightAreaX + 40, rightAreaStartY + 10, { align: 'center' }); // 45에서 40으로 변경
        this.addKoreanText('하단 오픈지로 페이지 바로가기 클릭하여 납부', rightAreaX + 40, rightAreaStartY + 18, { align: 'center' }); // 45에서 40으로 변경
        
        // 이번달 관리비 금액 정보 (중앙 정렬, 박스 너비에 맞춰 조정)
        const amountInfoY = rightAreaStartY + 35;
        this.doc.setFontSize(12);
        if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');
        this.addKoreanText('이번달에 납부하실 관리비 금액', rightAreaX + 40, amountInfoY, { align: 'center' }, 'bold'); // 45에서 40으로 변경
        
        this.doc.setFontSize(16);
        if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');
        this.addKoreanText(this.formatCurrency(Number(bill.totalAmount)), rightAreaX + 40, amountInfoY + 12, { align: 'center' }, 'bold');
        
        // 납부처 안내 섹션을 두 개의 박스로 분리 (너비 10pt 줄임)
        const noticeBoxWidth = 80; // 우측 영역 너비 (90에서 80으로 변경)
        
        // 1. 납부처 안내 제목과 오픈지로 안내문구 박스
        const noticeBoxStartY = rightAreaStartY - 5; // 제목 위쪽 여백
        const noticeBoxEndY = rightAreaStartY + 18 + 5; // 안내문구 아래쪽 여백
        const noticeBoxHeight = noticeBoxEndY - noticeBoxStartY;
        
        this.doc.setLineWidth(0.3);
        this.doc.rect(rightAreaX, noticeBoxStartY, noticeBoxWidth, noticeBoxHeight);
        
        // 2. 이번달 관리비 금액 박스
        const amountBoxStartY = amountInfoY - 5; // 금액 제목 위쪽 여백
        const amountBoxEndY = amountInfoY + 12 + 5; // 금액 아래쪽 여백
        const amountBoxHeight = amountBoxEndY - amountBoxStartY;
        
        this.doc.setLineWidth(0.3);
        this.doc.rect(rightAreaX, amountBoxStartY, noticeBoxWidth, amountBoxHeight);
        
        // 전체 영역을 감싸는 박스 (청구 내역, 납부처 안내, 관리 금액, 사용량, 미납금)
        const totalBoxStartY = finalStartY - 10; // 청구 내역 제목 위쪽 여백
        
        // 사용량 정보의 끝 위치 계산 (사용량 정보가 있는 경우)
        const usageInfoEndY = bill.usageInfo ? 190 + 8 * 4 + 5 : 0; // 사용량 정보 Y + 4행 * 8pt + 여백
        
        const totalBoxEndY = Math.max(
          usageInfoEndY, // 사용량 정보 끝
          amountBoxEndY + 5 // 납부처 안내 끝
        );
        const totalBoxHeight = totalBoxEndY - totalBoxStartY;
        
        // 우측 박스 영역의 오른쪽 끝에서 5pt 여백을 갖도록 너비 계산
        const rightBoxEndX = rightAreaX + noticeBoxWidth; // 105 + 80 = 185
        const totalBoxWidth = rightBoxEndX + 5 - 15; // 185 + 5 - 15 = 175 (시작 X=15에서 끝 X=190)
        
        this.doc.setLineWidth(0.3); // 0.5에서 0.3으로 변경
        this.doc.rect(15, totalBoxStartY, totalBoxWidth, totalBoxHeight);
      
    } catch (error) {
      console.error('[PDF] 청구 항목 생성 실패:', error);
    }
  }

  /**
   * 합계 생성
   */
  private generateTotal(bill: PortvilleBill): void {
    try {
      // 사용량 정보가 있으면 더 아래로, 없으면 기본 위치
      // 미납/연체 항목이 추가되어 더 아래로 이동 (10pt 위로 이동)
      const startY = bill.usageInfo ? 340 : 270;

      this.doc.setFontSize(14);
      if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');
      this.addKoreanText('총 청구금액', 20, startY, undefined, 'bold');
      
      this.doc.setFontSize(16);
      this.addKoreanText(this.formatCurrency(Number(bill.totalAmount)), 120, startY, undefined, 'bold');
      this.doc.setFontSize(10);
      this.addKoreanText('(부가세 포함)', 120, startY + 8);
      
    } catch (error) {
      console.error('[PDF] 합계 생성 실패:', error);
    }
  }

  /**
   * 푸터 생성
   */
  private generateFooter(bill: PortvilleBill): void {
    try {
      // 청구 내역 박스 하단에 푸터 배치
      // 안내문 시작 Y: 45, 안내문 높이: 약 50, 청구 내역 시작 Y: 55, 청구 내역 높이: 약 200
      // 전체 박스 높이를 고려하여 푸터 위치 계산
      const totalBoxStartY = 35; // 안내문 박스 시작 Y - 5
      const totalBoxHeight = 200; // 대략적인 전체 박스 높이
      const startY = totalBoxStartY + totalBoxHeight + 10; // 박스 하단에서 10pt 아래

      this.doc.setFontSize(16);
      if (this.isKoreanFontReady) this.doc.setFont('NotoSansKR', 'bold');

      this.addKoreanText('(주) 아스타아이비에스 대표이사 (직인생략)', 15 + 175/2, startY, { align: 'center' }, 'bold');

    } catch (error) {
      console.error('[PDF] 푸터 생성 실패:', error);
    }
  }

  /**
   * 통화 포맷팅 - 한글 원화 표시 (숫자와 원 사이 공백 포함)
   */
  private formatCurrency(amount: number): string {
    try {
      // 한국 원화 포맷팅
      const formatted = new Intl.NumberFormat('ko-KR').format(amount);
      return `${formatted} 원`;
    } catch (error) {
      console.warn('[PDF] 통화 포맷팅 실패:', error);
      return `${amount} 원`;
    }
  }

  /**
   * 상태 텍스트 변환
   */
  private getStatusText(status: string): string {
    switch (status) {
      case 'pending': return '미납';
      case 'paid': return '납부완료';
      case 'overdue': return '연체';
      case 'cancelled': return '취소';
      default: return status;
    }
  }
}

/**
 * 청구서 PDF 생성 함수
 */
export const generateBillPDF = async (bill: PortvilleBill, encoding: 'EUC-KR' | 'UTF-8' = 'UTF-8'): Promise<void> => {
  const generator = new BillPDFGenerator(encoding);
  
  // 한글 폰트 설정 시도
  const fontLoaded = await (generator as any).setupKoreanFont();
  
  if (!fontLoaded) {
    console.warn('[PDF] 한글 폰트 로딩 실패 - 기본 폰트로 생성됩니다.');
  }

  // 새 문서의 첫 페이지에 바로 렌더
  generator.generateBillPDF(bill);

  const fileName = `청구서_${bill.roomNumber}_${bill.billingPeriod.year}년${bill.billingPeriod.month}월.pdf`;
  (generator as any)['doc'].save(fileName);
};

/**
 * 여러 청구서를 하나의 PDF로 생성
 */
export const generateMultipleBillsPDF = async (bills: PortvilleBill[], encoding: 'EUC-KR' | 'UTF-8' = 'UTF-8'): Promise<void> => {
  if (!bills || bills.length === 0) {
    console.warn('[PDF] 생성할 청구서가 없습니다.');
    return;
  }

  const generator = new BillPDFGenerator(encoding);
  
  // 한글 폰트 설정 시도
  const fontLoaded = await (generator as any).setupKoreanFont();
  
  if (!fontLoaded) {
    console.warn('[PDF] 한글 폰트 로딩 실패 - 기본 폰트로 생성됩니다.');
  }

  bills.forEach((bill, index) => {
    if (index > 0) {
      (generator as any)['doc'].addPage();
    }
    generator.generateBillPDF(bill);
  });

  const fileName = `청구서_${bills.length}건_${new Date().toLocaleDateString('ko-KR').replace(/\./g, '')}.pdf`;
  (generator as any)['doc'].save(fileName);
};
